<template>
	<div class="booker-date">
		<div class="booker-date__header">
			<GlobalModalHeader class="booker-date__header-title" @closeModal="closeMenu">{{
				$t('components.booker.date_container.select_dates')
			}}</GlobalModalHeader>
			<div class="booker-date__calendar-days">
				<span v-for="(day, i) in days" :key="i">{{ day.short }}</span>
			</div>
		</div>
		<div class="booker-date__calendar" :style="{ '--offset': isSavingBarState || isMinimumHoursBarState ? '70px' : '0px' }">
			<GlobalBookerCalendar
				:startDate="bookerState.selectedDates.start"
				:endDate="bookerState.selectedDates.end"
				@updateCalendar="updateCalendar"></GlobalBookerCalendar>
		</div>
		<v-divider></v-divider>
		<div class="booker-date__footer">
			<div class="booker-date__info-bars">
				<TransitionGroup name="fade" mode="out-in">
					<GlobalBookerInfoBar v-if="isSavingBarState" :data="savingBarData" @closeBar="isSavingBarWasClosed = true"></GlobalBookerInfoBar>
					<GlobalBookerInfoBar
						v-else-if="isMinimumHoursBarState && !isMinimumHoursBarWasClosed"
						:data="mimimumHoursBarData"
						@closeBar="isMinimumHoursBarWasClosed = true"></GlobalBookerInfoBar>
					<GlobalBookerInfoBar
						v-if="isMinimumDaysBarState && !isMinimumDaysBarWasClosed"
						:data="minimumSegmentDaysBarData"
						@closeBar="isMinimumDaysBarWasClosed = true"></GlobalBookerInfoBar>
				</TransitionGroup>

				<Transition name="fade" mode="out-in">
					<GlobalBookerInfoBar
						v-if="isRenteonDateError || isExpiredDateBarState"
						:data="renteonDateError"
						notCloseable></GlobalBookerInfoBar>
				</Transition>
			</div>
			<div class="booker-date__times">
				<div class="booker-date__time">
					<div class="booker-date__input-container">
						<div class="booker-date__overlay" @click="isStartTimeSelectOpen = true">
							<InputPrimary
								v-model="startTime"
								:label="$t('components.booker.date_container.pickup_time_label')"
								class="booker-date__input"
								:class="{ 'booker-date__input--highlighted': areTimesHighlighted.start }">
							</InputPrimary>
						</div>
						<GlobalBookerDateSelect
							v-model="isStartTimeSelectOpen"
							:type="EBookerLocationType.START"
							:initialTime
							:currentDate="startDate"
							@selectTime="(v, notUserAction) => selectTime(EBookerLocationType.START, v, notUserAction)" />
					</div>
					<div class="booker-date__input-container">
						<div class="booker-date__overlay" @click="isEndTimeSelectOpen = true">
							<InputPrimary
								v-model="endTime"
								:label="$t('components.booker.date_container.dropoff_time_label')"
								class="booker-date__input"
								:class="{ 'booker-date__input--highlighted': areTimesHighlighted.end }"></InputPrimary>
						</div>

						<GlobalBookerDateSelect
							v-model="isEndTimeSelectOpen"
							:initialTime
							:type="EBookerLocationType.END"
							@selectTime="(v) => selectTime(EBookerLocationType.END, v)" />
					</div>
				</div>
				<div class="booker-date__button">
					<ButtonPrimary :disabled="!canSaveDates" @click="saveDates">{{
						$t('components.booker.date_container.confirm_selection')
					}}</ButtonPrimary>
				</div>
			</div>
		</div>
	</div>
</template>

<script lang="ts" setup>
import { differenceInHours, differenceInMinutes, isBefore } from 'date-fns'
import { EBookerLocationType } from '@/data/bookerData'

const megaMenuStore = useMegaMenuStore()
const bookingStore = useBookingStore()
const $t = useI18n().t
const isDateMenuOpen = defineModel<boolean>()
const bookerStore = useBookerStore()
const startDate = ref()
const endDate = ref()
const startTime = ref()
const endTime = ref()
const isStartTimeSelectOpen = ref(false)
const isEndTimeSelectOpen = ref(false)
const router = useRouter()
const isSavingBarWasClosed = ref(false)
const isMinimumHoursBarWasClosed = ref(false)
const isMinimumDaysBarWasClosed = ref(false)
const isRenteonDateError = computed(() => bookerStore.getIsRenteonDateError)
const localePath = useLocalePath()
const areTimesHighlighted = ref({
	start: false,
	end: false,
})
const { getStartDate } = useBookerService()

const timeToNumber = (timeString: string) => {
	const [hours, minutes] = timeString.split(':').map(Number)
	return hours * 60 + minutes
}

const countDays = computed(() => {
	if (!endDateAndTime.value || !startDateAndTime.value) return 0
	return Math.ceil(differenceInMinutes(endDateAndTime.value, startDateAndTime.value - 30) / 60 / 24)
})

const minimumHours = 24
const getNumberWithPrefix = (number: number) => {
	return number < 10 ? `0${number}` : number
}
const bookerState = inject<Ref<IBookerState>>('bookerState')!
const initialTime = computed(() => {
	if (!bookerState.value.selectedDates) return { start: '00:00', end: '00:00' }
	const dates = bookerState.value.selectedDates
	if (!dates.start || !dates.end) return { start: '00:00', end: '00:00' }
	const start = `${getNumberWithPrefix(new Date(dates.start).getHours())}:${getNumberWithPrefix(new Date(dates.start).getMinutes())}`
	const end = `${getNumberWithPrefix(new Date(dates.end).getHours())}:${getNumberWithPrefix(new Date(dates.end).getMinutes())}`
	return { start, end }
})
startDate.value = bookerState.value.selectedDates.start
startTime.value = initialTime.value.start
endDate.value = bookerState.value.selectedDates.end
endTime.value = initialTime.value.end

const freeHoursForSavingInfo = 0.5 * 60 // changed from 1h to 30 min because renteon ¯\_(ツ)_/¯
const maxHoursForSavingInfo = 6 * 60

const isSavingBarState = computed(() => {
	if (countDays.value <= 1) return false
	if (isSavingBarWasClosed.value) return false
	if (!startTime.value || !endTime.value) return false
	const start = timeToNumber(startTime.value)
	const end = timeToNumber(endTime.value)
	if (end - start <= maxHoursForSavingInfo && end - start > freeHoursForSavingInfo) return (end - start - freeHoursForSavingInfo) / 60
	const day = 24 * 60
	if (day - start <= maxHoursForSavingInfo && end <= maxHoursForSavingInfo) {
		if (day - start + end <= maxHoursForSavingInfo) return (day - start + end - freeHoursForSavingInfo) / 60
	}
	return false
})

const isMinimumHoursBarState = computed(() => {
	if (!endDateAndTime.value || !startDateAndTime.value) return 0
	return differenceInHours(endDateAndTime.value, startDateAndTime.value) < 24

	// if (countDays.value !== 1 && countDays.value !== 0) return false
	// if (!startTime.value || !endTime.value) return false
	// const start = timeToNumber(startTime.value)
	// const end = timeToNumber(endTime.value)
	// return end - start <= 60 * minimumHours && end - start >= 0
})

const isMinimumDaysBarState = computed(() => {
	if (!endDateAndTime.value || !startDateAndTime.value) return 0
	const selectedBookingCar = bookingStore.getSelectedCar
	const minimumSegmentDays = selectedBookingCar?.segment.minDays

	const minHours = minimumSegmentDays ? minimumSegmentDays * 24 : 0

	if (!minHours) return false

	return differenceInHours(endDateAndTime.value, startDateAndTime.value) < minHours
})

const isExpiredDateBarState = computed(() => {
	if (!startDateAndTime.value) return false
	return isBefore(startDateAndTime.value, getStartDate())
})

const startDateAndTime = computed(() => {
	if (!startDate.value || !startTime.value) return null
	const start = new Date(startDate.value)
	start.setHours(startTime.value.split(':')[0])
	start.setMinutes(startTime.value.split(':')[1])
	return start
})

const endDateAndTime = computed(() => {
	if (!endDate.value || !endTime.value) return null
	const end = new Date(endDate.value)
	end.setHours(endTime.value.split(':')[0])
	end.setMinutes(endTime.value.split(':')[1])
	return end
})

// watch(
// 	() => isMinimumHoursBarState.value,
// 	() => {
// 		if (isMinimumHoursBarState.value) endTime.value = numberToTime(timeToNumber(startTime.value) + 60 * minimumHours)
// 	},
// 	{ immediate: true },
// )

// const numberToTime = (number: number) => {
// 	const hours = Math.floor(number / 60)
// 	const minutes = number % 60
// 	return `${getNumberWithPrefix(hours)}:${getNumberWithPrefix(minutes)}`
// }

const updateCalendar = (times: any) => {
	startDate.value = times.start
	endDate.value = times.end
	areTimesHighlighted.value = { start: true, end: true }
}

const days = ref([
	{ short: 'Pn', long: 'Poniedziałek' },
	{ short: 'Wt', long: 'Wtorek' },
	{ short: 'Śr', long: 'Środa' },
	{ short: 'Czw', long: 'Czwartek' },
	{ short: 'Pt', long: 'Piątek' },
	{ short: 'So', long: 'Sobota' },
	{ short: 'Nd', long: 'Niedziela' },
])

const savingBarData = computed(() => {
	return {
		title: $t('components.booker.date_container.choose_less_and_save'),
		desc: `${$t('components.booker.date_container.if_you_take')} ${isSavingBarState.value}${$t('components.booker.date_container.you_will_pay_for')} ${countDays.value - 1} ${$t('common.days')}`,
		icon: 'clock',
	}
})

const mimimumHoursBarData = computed(() => {
	return {
		title: `${$t('components.booker.date_container.minimum_rental_time_is')} ${minimumHours} ${$t('common.hours')}`,
		icon: 'clock',
	}
})

const minimumSegmentDaysBarData = computed(() => {
	const selectedBookingCar = bookingStore.getSelectedCar
	const minimumSegmentDays = selectedBookingCar?.segment.minDays
	return {
		title: `${$t('components.booker.date_container.minimum_car_rental_days_is')} ${minimumSegmentDays} ${$t('common.days')}.`,
		desc: $t('components.booker.date_container.choose_more_days_or'),
		icon: 'clock',
		button: {
			title: $t('components.booker.date_container.change_segment'),
			link: localePath({ name: 'cars' }),
		},
	}
})

const renteonDateError = computed(() => {
	return {
		title: $t('components.booker.date_container.booking_time_has_expired'),
		icon: 'clock',
	}
})

const selectTime = (type: EBookerLocationType, value: any, notUserAction?: boolean) => {
	if (type === EBookerLocationType.START) startTime.value = value
	else endTime.value = value
	// if (isMinimumHoursBarState.value) endTime.value = numberToTime(timeToNumber(startTime.value) + 60 * minimumHours)
	isSavingBarWasClosed.value = false
	isMinimumHoursBarWasClosed.value = false
	if (!notUserAction) {
		areTimesHighlighted.value[type] = false
	}
}

const canSaveDates = computed(() => {
	if (!startDate.value || !endDate.value || !startTime.value || !endTime.value) return false
	if (isMinimumHoursBarState.value) return false
	// if (startDateAndTime.value && isBefore(startDateAndTime.value, getStartDate())) return false
	if (isExpiredDateBarState.value) return false
	if (isMinimumDaysBarState.value) return false
	return true
})

const saveDates = () => {
	if (!startDateAndTime.value || !endDateAndTime.value) return
	bookerState.value.selectedDates = { start: startDateAndTime.value, end: endDateAndTime.value }
	bookerStore.setIsRenteonDateError(false)
	bookingStore.setBookerDates(bookerState.value.selectedDates)
	isDateMenuOpen.value = false
	bookingStore.fetchBooking()
	megaMenuStore.clearCars()

	if (bookerStore.getIsRedirectToReservationAfterDate) {
		bookerStore.setIsRedirectToReservationAfterDate(false)
		router.push(localePath({ name: 'reservation' }))
	}
}

const closeMenu = () => {
	isDateMenuOpen.value = false
	if (router.currentRoute.value.hash === '#date' || router.currentRoute.value.hash === '#location') router.go(-1)
}

const closeMenuBrowser = () => {
	isDateMenuOpen.value = false
}

if (process.client) {
	window.addEventListener('popstate', closeMenuBrowser)
}

const intercomService = IntercomService()
const { isMobile_lg } = useWindowWidth()

onMounted(() => {
	if (isMobile_lg.value) {
		intercomService.setIntercomPosition(75, 20)
	}
})

onUnmounted(() => {
	window.removeEventListener('popstate', closeMenuBrowser)
	intercomService.setIntercomPosition()
})
</script>

<style lang="scss">
@import './DateContainer';
</style>
